// ANGEL FLIGHT — shared FLIR DOM UI kit for the menu screens.
//
// One source of truth for the sensor-overlay look the screens share with
// render/HudOverlay.ts: the HUD green / warn palette, the ui-monospace stack,
// letter-spacing + text-shadow, safe-area padding. The helpers (makeButton,
// makePanel, makeScreenRoot, …) build plain DOM with inline styles and return a
// small, disposable handle — no framework, no globals. Every element is created
// here so the screens stay DRY and visually consistent.

/** FLIR sensor palette — mirrors HudOverlay's HUD_GREEN / HUD_WARN. */
export const FLIR = Object.freeze({
  /** Primary readout green. */
  green: '#9effa8',
  /** Dimmed green for secondary text. */
  greenDim: 'rgba(158,255,168,0.55)',
  /** Faint green for hairlines / disabled chrome. */
  greenFaint: 'rgba(158,255,168,0.22)',
  /** Warning / failure orange-red. */
  warn: '#ff7a4d',
  /** Cool accent used for the deploy CTA glow. */
  accent: '#ffd08a',
  /** Near-black sensor background wash. */
  bg: '#05070b',
  /** Panel fill. */
  panel: 'rgba(8,14,11,0.82)',
  /** Slightly brighter panel fill for rows / hover. */
  panelHi: 'rgba(18,30,22,0.9)',
} as const);

/** The UI font stack across the HUD + screens. A clean system SANS (calmer +
 *  far easier to read than the old wide-tracked monospace the owner flagged as
 *  "too aggressive"); no web-font download. FONT_MONO is reserved for figures
 *  where digit alignment helps (timers / ammo). */
export const FONT =
  "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

/** Tabular monospace for numeric readouts only (timecodes, counters). */
export const FONT_MONO =
  "ui-monospace, 'SFMono-Regular', Menlo, monospace";

/** Standard letter-spacing — eased right down (0.12em → 0.03em) so text reads
 *  calmly instead of stretched-out and tactical-shouty. */
export const TRACKING = '0.03em';

/** A drop-shadow that keeps text legible over the thermal canvas. */
export const TEXT_SHADOW = '0 0 6px rgba(0,0,0,0.9)';

/** Safe-area padding string shared with the HUD. */
export const SAFE_PAD =
  'max(env(safe-area-inset-top), 22px) max(env(safe-area-inset-right), 26px) max(env(safe-area-inset-bottom), 22px) max(env(safe-area-inset-left), 26px)';

type Style = Partial<CSSStyleDeclaration>;

/** Apply a partial style object to an element (typed convenience wrapper). */
export function applyStyle(el: HTMLElement, style: Style): void {
  Object.assign(el.style, style);
}

/** Create an element with optional class-free inline styles + text. */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  style?: Style,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (style) Object.assign(node.style, style);
  if (text !== undefined) node.textContent = text;
  return node;
}

/**
 * A full-screen FLIR screen root: absolute, fills the host, monospace green,
 * vertical-centred column with safe-area padding. `pointer-events` is `auto`
 * on the root so its own background captures stray taps (children opt out as
 * needed). Returns the root div — append your content, then add it to a parent.
 */
export function makeScreenRoot(opts: { center?: boolean } = {}): HTMLDivElement {
  const root = el('div', {
    position: 'absolute',
    inset: '0',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: opts.center === false ? 'flex-start' : 'center',
    gap: '18px',
    padding: SAFE_PAD,
    font: `600 14px/1.4 ${FONT}`,
    letterSpacing: TRACKING,
    color: FLIR.green,
    textShadow: TEXT_SHADOW,
    // A translucent scrim instead of the old opaque near-black wash (G2 menu
    // attract): the live ColorWorld scrolling behind the menus stays visible —
    // darker at the top/bottom bands where the text lives, thinnest mid-frame
    // where the battlefield reads through.
    background:
      'linear-gradient(180deg, rgba(5,7,11,0.86) 0%, rgba(5,7,11,0.58) 38%, rgba(5,7,11,0.58) 62%, rgba(5,7,11,0.88) 100%)',
    pointerEvents: 'auto',
    overflow: 'hidden',
  });
  return root;
}

/** The big title line (e.g. "ANGEL FLIGHT"). */
export function makeTitle(text: string, color: string = FLIR.green): HTMLDivElement {
  return el(
    'div',
    {
      font: `700 clamp(34px, 7vw, 52px)/1.04 ${FONT}`,
      letterSpacing: '0.06em',
      // Trailing tracking pad: re-centre the title optically (the last letter's
      // tracking space would otherwise push it left of the buttons' axis).
      paddingLeft: '0.06em',
      color,
      textAlign: 'center',
      // A soft glow keeps the big type legible over the live attract world.
      textShadow: '0 0 18px rgba(0,0,0,0.95), 0 2px 6px rgba(0,0,0,0.9)',
    },
    text,
  );
}

/** A dim subtitle / tagline line. */
export function makeSubtitle(text: string): HTMLDivElement {
  return el(
    'div',
    {
      font: `500 clamp(13px, 2.4vw, 16px)/1.5 ${FONT}`,
      letterSpacing: '0.04em',
      color: FLIR.greenDim,
      textAlign: 'center',
    },
    text,
  );
}

/** A boxed panel with the thin green FLIR outline. Append children to it. */
export function makePanel(style: Style = {}): HTMLDivElement {
  return el('div', {
    boxSizing: 'border-box',
    background: FLIR.panel,
    border: `1px solid ${FLIR.greenFaint}`,
    borderRadius: '8px',
    padding: '18px 22px',
    ...style,
  });
}

/** Handle for a button: the element plus a disposer that drops its listeners. */
export interface ButtonHandle {
  el: HTMLButtonElement;
  setEnabled(enabled: boolean): void;
  setLabel(label: string): void;
  destroy(): void;
}

export interface ButtonOpts {
  /** Accent (filled) CTA styling vs the default outline. */
  accent?: boolean;
  /** Start disabled (greyed, non-interactive). */
  enabled?: boolean;
  /** Explicit width (CSS string). Defaults to a comfortable touch width. */
  width?: string;
  /** Explicit min-height (CSS string). iPad-first default is 56px. */
  minHeight?: string;
}

/**
 * A large, touch-friendly FLIR button. Thin green outline (or filled accent),
 * monospace, with hover + active states wired via listeners that the returned
 * handle's `destroy()` removes. `pointer-events:auto` so it works under a
 * `pointer-events:none` HUD layer.
 */
export function makeButton(
  label: string,
  onTap: () => void,
  opts: ButtonOpts = {},
): ButtonHandle {
  const accent = opts.accent ?? false;
  let enabled = opts.enabled ?? true;

  const btn = el('button', {
    appearance: 'none',
    webkitAppearance: 'none',
    boxSizing: 'border-box',
    width: opts.width ?? 'min(360px, 80vw)',
    minHeight: opts.minHeight ?? '56px',
    padding: '14px 24px',
    font: `600 clamp(15px, 2.6vw, 19px)/1 ${FONT}`,
    letterSpacing: '0.05em',
    cursor: 'pointer',
    borderRadius: '10px',
    textShadow: TEXT_SHADOW,
    pointerEvents: 'auto',
    touchAction: 'manipulation',
    transition: 'background 120ms, border-color 120ms, color 120ms, transform 80ms',
    userSelect: 'none',
  });

  const paint = (hover: boolean, active: boolean): void => {
    if (!enabled) {
      btn.style.background = 'rgba(8,14,11,0.5)';
      btn.style.border = `1px solid ${FLIR.greenFaint}`;
      btn.style.color = FLIR.greenFaint;
      btn.style.cursor = 'default';
      btn.style.transform = 'none';
      return;
    }
    btn.style.cursor = 'pointer';
    btn.style.transform = active ? 'translateY(1px)' : 'none';
    if (accent) {
      btn.style.background = hover ? 'rgba(255,208,138,0.22)' : 'rgba(255,208,138,0.12)';
      btn.style.border = `1px solid ${FLIR.accent}`;
      btn.style.color = FLIR.accent;
    } else {
      btn.style.background = hover ? FLIR.panelHi : FLIR.panel;
      btn.style.border = `1px solid ${hover ? FLIR.green : FLIR.greenFaint}`;
      btn.style.color = FLIR.green;
    }
  };

  const onEnter = (): void => paint(true, false);
  const onLeave = (): void => paint(false, false);
  const onDown = (): void => paint(true, true);
  const onUp = (): void => paint(true, false);
  const onClick = (): void => {
    if (enabled) onTap();
  };

  btn.addEventListener('pointerenter', onEnter);
  btn.addEventListener('pointerleave', onLeave);
  btn.addEventListener('pointerdown', onDown);
  btn.addEventListener('pointerup', onUp);
  btn.addEventListener('click', onClick);

  btn.textContent = label;
  paint(false, false);

  return {
    el: btn,
    setEnabled(next: boolean): void {
      enabled = next;
      btn.disabled = !next;
      paint(false, false);
    },
    setLabel(next: string): void {
      btn.textContent = next;
    },
    destroy(): void {
      btn.removeEventListener('pointerenter', onEnter);
      btn.removeEventListener('pointerleave', onLeave);
      btn.removeEventListener('pointerdown', onDown);
      btn.removeEventListener('pointerup', onUp);
      btn.removeEventListener('click', onClick);
      btn.remove();
    },
  };
}

/** Handle for a labelled on/off toggle (settings rows). */
export interface ToggleHandle {
  el: HTMLDivElement;
  set(on: boolean): void;
  destroy(): void;
}

/**
 * A FLIR settings toggle: a label and a [ ON ]/[ OFF ] pill. `onChange` fires
 * with the new value on tap; `initial` paints the starting state.
 */
export function makeToggle(
  label: string,
  initial: boolean,
  onChange: (on: boolean) => void,
): ToggleHandle {
  let on = initial;

  const row = el('div', {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '20px',
    width: '100%',
    padding: '6px 0',
  });

  const name = el(
    'div',
    { font: `600 15px/1 ${FONT}`, letterSpacing: '0.05em', color: FLIR.green },
    label,
  );

  const pill = el('button', {
    appearance: 'none',
    webkitAppearance: 'none',
    minWidth: '88px',
    minHeight: '40px',
    padding: '8px 16px',
    font: `700 14px/1 ${FONT}`,
    letterSpacing: '0.16em',
    borderRadius: '8px',
    cursor: 'pointer',
    pointerEvents: 'auto',
    touchAction: 'manipulation',
    userSelect: 'none',
    textShadow: TEXT_SHADOW,
  });

  const paint = (): void => {
    pill.textContent = on ? 'ON' : 'OFF';
    pill.style.color = on ? FLIR.green : FLIR.greenDim;
    pill.style.border = `1px solid ${on ? FLIR.green : FLIR.greenFaint}`;
    pill.style.background = on ? 'rgba(158,255,168,0.14)' : 'rgba(8,14,11,0.6)';
  };

  const onClick = (): void => {
    on = !on;
    paint();
    onChange(on);
  };
  pill.addEventListener('click', onClick);
  paint();

  row.append(name, pill);

  return {
    el: row,
    set(next: boolean): void {
      on = next;
      paint();
    },
    destroy(): void {
      pill.removeEventListener('click', onClick);
      row.remove();
    },
  };
}

/** Handle for a labelled 0..1 slider (settings rows). */
export interface SliderHandle {
  el: HTMLDivElement;
  set(value: number): void;
  destroy(): void;
}

/**
 * A FLIR settings slider: label + a native range input restyled to the sensor
 * look + a live percentage readout. `onChange` fires with the new 0..1 value
 * on input (continuous while dragging — volume changes should be audible live).
 */
export function makeSlider(
  label: string,
  initial: number,
  onChange: (value: number) => void,
): SliderHandle {
  const row = el('div', {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '14px',
    width: '100%',
    padding: '6px 0',
  });

  const name = el(
    'div',
    { font: `600 15px/1 ${FONT}`, letterSpacing: '0.05em', color: FLIR.green, flex: '0 0 auto' },
    label,
  );

  const input = document.createElement('input');
  input.type = 'range';
  input.min = '0';
  input.max = '100';
  input.value = `${Math.round(initial * 100)}`;
  Object.assign(input.style, {
    flex: '1 1 auto',
    accentColor: FLIR.green,
    pointerEvents: 'auto',
    touchAction: 'manipulation',
    minHeight: '40px', // comfortable touch target
    cursor: 'pointer',
  } as CSSStyleDeclaration);

  const readout = el(
    'div',
    {
      font: `700 13px/1 ${FONT}`,
      letterSpacing: '0.1em',
      color: FLIR.greenDim,
      minWidth: '44px',
      textAlign: 'right',
      flex: '0 0 auto',
    },
    `${Math.round(initial * 100)}%`,
  );

  const onInput = (): void => {
    const v = Math.max(0, Math.min(1, Number(input.value) / 100));
    readout.textContent = `${Math.round(v * 100)}%`;
    onChange(v);
  };
  input.addEventListener('input', onInput);

  row.append(name, input, readout);
  return {
    el: row,
    set(value: number): void {
      input.value = `${Math.round(value * 100)}`;
      readout.textContent = `${Math.round(value * 100)}%`;
    },
    destroy(): void {
      input.removeEventListener('input', onInput);
      row.remove();
    },
  };
}

/** Handle for a segmented option row (e.g. difficulty). */
export interface SegmentedHandle {
  el: HTMLDivElement;
  set(value: string): void;
  destroy(): void;
}

/**
 * A FLIR segmented control: a label + one pill per option; tapping selects it
 * and fires `onChange` with the option's value. Used for difficulty.
 */
export function makeSegmented(
  label: string,
  options: ReadonlyArray<{ value: string; label: string }>,
  initial: string,
  onChange: (value: string) => void,
): SegmentedHandle {
  let current = initial;

  const row = el('div', {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '14px',
    width: '100%',
    padding: '6px 0',
    flexWrap: 'wrap',
  });

  const name = el(
    'div',
    { font: `600 15px/1 ${FONT}`, letterSpacing: '0.05em', color: FLIR.green, flex: '0 0 auto' },
    label,
  );

  const group = el('div', { display: 'flex', gap: '6px', flex: '0 0 auto' });
  const pills: Array<{ value: string; pill: HTMLButtonElement; fn: () => void }> = [];

  const paint = (): void => {
    for (const p of pills) {
      const on = p.value === current;
      p.pill.style.color = on ? FLIR.accent : FLIR.greenDim;
      p.pill.style.border = `1px solid ${on ? FLIR.accent : FLIR.greenFaint}`;
      p.pill.style.background = on ? 'rgba(255,208,138,0.14)' : 'rgba(8,14,11,0.6)';
    }
  };

  for (const opt of options) {
    const pill = el('button', {
      appearance: 'none',
      webkitAppearance: 'none',
      minHeight: '40px',
      padding: '8px 12px',
      font: `700 13px/1 ${FONT}`,
      letterSpacing: '0.12em',
      borderRadius: '8px',
      cursor: 'pointer',
      pointerEvents: 'auto',
      touchAction: 'manipulation',
      userSelect: 'none',
      textShadow: TEXT_SHADOW,
    });
    pill.textContent = opt.label;
    const fn = (): void => {
      current = opt.value;
      paint();
      onChange(opt.value);
    };
    pill.addEventListener('click', fn);
    pills.push({ value: opt.value, pill, fn });
    group.appendChild(pill);
  }
  paint();

  row.append(name, group);
  return {
    el: row,
    set(value: string): void {
      current = value;
      paint();
    },
    destroy(): void {
      for (const p of pills) p.pill.removeEventListener('click', p.fn);
      row.remove();
    },
  };
}

/** A scrip/salvage footer line (dim, centred). */
export function makeCurrencyLine(scrip: number, salvage: number): HTMLDivElement {
  return el(
    'div',
    {
      font: `600 14px/1 ${FONT}`,
      letterSpacing: '0.05em',
      color: FLIR.greenDim,
      textAlign: 'center',
    },
    `SCRIP ${scrip}    ·    SALVAGE ${salvage}`,
  );
}

/** Format milliseconds as M:SS (matches the HUD timecode). */
export function formatTime(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
